import { createElement, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import { ACTION_CELL_CLASS, CARD_CLASS } from './content';
import { InventoryReplenishmentRoot } from './styles';

export { buildInventoryReplenishmentHtml } from './content';

/** 레거시 문서 카드의 버튼 */
const LEGACY_DELETE_CLASS = 'btn-detail-hide';
const LEGACY_REJECT_CLASS = 'btn-detail-reject';

export type InventoryReplenishmentTarget = { itemId: string; docTit: string };

/** 부분반려 버튼을 붙일 카드 수 — V2 카드 + 레거시 부분반려 버튼 */
export const countInventoryReplenishmentCards = (content: string | null) =>
  content
    ? (
        content.match(
          new RegExp(`${CARD_CLASS}"|${LEGACY_REJECT_CLASS}`, 'g'),
        ) ?? []
      ).length
    : 0;

/**
 * 저장 카드 HTML 그대로(레거시 문서 포함). 삭제 버튼은 작성 화면만,
 * 카드별 버튼(부분반려)은 조회 화면만
 */
export const InventoryReplenishmentSnapshot = ({
  content,
  onDelete,
  renderCardAction,
}: {
  content: string;
  onDelete?: (target: InventoryReplenishmentTarget) => void;
  renderCardAction?: (target: InventoryReplenishmentTarget) => ReactNode;
}) => {
  const { t } = useTranslation();

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      const { classList } = element;
      if (classList.contains(LEGACY_DELETE_CLASS)) return null;
      if (classList.contains(LEGACY_REJECT_CLASS)) {
        const itemId = element.getAttribute('data-aprv_id') ?? '';
        return itemId
          ? renderCardAction?.({
              itemId,
              docTit: element.getAttribute('data-aprv_doctit') ?? '',
            }) ?? null
          : null;
      }
      if (!classList.contains(ACTION_CELL_CLASS)) return undefined;
      const card = element.closest(`.${CARD_CLASS}`);
      const itemId = card?.getAttribute('data-item-id') ?? '';
      const docTit = card?.getAttribute('data-doc-tit') ?? '';
      return createElement(
        tagName,
        props,
        onDelete && itemId ? (
          <BoxButton
            key="delete"
            type="button"
            variant="secondary"
            size="small"
            onClick={() => onDelete({ itemId, docTit })}
          >
            {t('삭제')}
          </BoxButton>
        ) : null,
        itemId ? renderCardAction?.({ itemId, docTit }) ?? null : null,
      );
    },
    [onDelete, renderCardAction, t],
  );

  return (
    <InventoryReplenishmentRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </InventoryReplenishmentRoot>
  );
};
